// frontend/src/pages/JobsPage.js
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-07-08
// Updated:      2026-10-08 10:20 BST
//
// Description:  Job dashboard page to manage uploads, job creation, and progress tracking.
//
// Change Summary (2026-10-08 10:20 BST):
//   - Authoritative Git/current audit confirmed no job_form-specific desktop sizing branch remains.
//   - Desktop GridLayout remains globally resizable/draggable with no maxRows ceiling; no Job Form host-height mutation is performed here.
//
// Change Summary (2026-10-08 09:31 BST):
//   - Verified JobsPage grid sizing path against the pre-Work-Package/Git version: no Job Form-specific constraints remain.
//
// Change Summary (2026-10-08 09:19 BST):
//   - Restored the proven generic ReactGridLayout sizing contract used by working widgets.
//   - Removed all Job Form-specific layout normalisation/resize flags introduced during the 07 Oct corrective passes.
//   - JobsPage no longer rewrites Job Form minH/static/isResizable/isDraggable/maxH during layout restore/change.
//   - Desktop resize ownership remains entirely with ReactGridLayout; mobile remains natural single-column presentation.
//
// Desktop resize regression fix (2026-09-23):
//   - Removed the viewport-derived ReactGridLayout `maxRows` ceiling.
//   - Desktop widgets may grow vertically beyond the initial viewport and the page scrolls naturally.
//   - Layout persistence still preserves x/y/w/h and prevents negative y positions.
//   - New widgets are appended below the current layout instead of being forced into viewport rows.
//   - Mobile remains a non-editable single-column presentation.
//
// Mobile Responsiveness (2026-09-04):
//   - Added a dedicated mobile presentation at <= 768px.
//   - Mobile renders the user's configured Job widgets in a natural single-column flow.
//   - Mobile ordering follows the persisted desktop layout position (y, then x).
//   - react-grid-layout drag/resize behaviour remains desktop-only.
//   - Mobile rendering never persists layout changes, so viewing Jobs on a phone cannot
//     alter the user's desktop widget arrangement.
//   - Reflowed the page heading and Add Widget action for compact mobile widths.
//   - Reduced mobile page gutters while preserving all existing Job widgets, widget
//     loading behaviour, localStorage keys, Add Widget flow, and desktop layout behaviour.
// Q-22A (2026-09-03):
//   - Removed hard-coded JobProgressWidget test context (jobId=1 / TEST_JOB_001).
//   - JobsPage currently exposes no selected/active job context.
//   - JobProgressWidget therefore mounts without a job and uses its existing safe
//     "No job has been selected" state instead of polling a fabricated job.
// ✅ FIX: Added job_list back to resolveWidgetComponent and AddWidgetModal list.
// ✅ NEW: Added taxonomy_admin to import, resolver, and AddWidgetModal so it loads properly.
// ✅ NEW (2026-10-05): Added work_package_taxonomy_admin to the JobsPage import, resolver, and AddWidgetModal allow-list.
// ✅ NEW (2026-03-02): Added authority_directory widget for licensing/admin boundary directory maintenance.
// ✅ NEW (2026-03-04): Added weather_spot_widget (Met Office Weather) using pinned location.
// ✅ NEW (2026-03-06): Added flood_intelligence_widget to imports, defaults, resolver and AddWidgetModal.
// ✅ NEW (2026-03-10): Added traffic_intelligence_widget to imports, defaults, resolver and AddWidgetModal.

import React, { useState, useEffect, useCallback, useRef } from "react";
import GridLayout from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

import WidgetWrapper from "../components/WidgetWrapper";
import AddWidgetModal from "../components/AddWidgetModal";
import { markWidgetUsedIn } from "../widgets/WidgetRegistry";

import JobFormWidget from "../widgets/JobFormWidget";
import JobFileUploadWidget from "../widgets/JobFileUploadWidget";
import JobListWidget from "../widgets/JobListWidget"; // ✅ RE-ADDED
import ContactFormWidget from "../widgets/ContactFormWidget";
import JobProgressWidget from "../widgets/JobProgressWidget";
import AdvancedTaskListWidget from "../widgets/AdvancedTaskListWidget";
import MyTasksWidget from "../widgets/MyTasksWidget";
import WorkPackageProgressWidget from "../widgets/WorkPackageProgressWidget";
// ✅ NEW: Taxonomy Admin widget (required for proper loading)
import TaxonomyAdminWidget from "../widgets/TaxonomyAdminWidget";
import WorkPackageTaxonomyAdminWidget from "../widgets/WorkPackageTaxonomyAdminWidget";

// ✅ NEW: Authority Directory widget
import AuthorityDirectoryWidget from "../widgets/AuthorityDirectoryWidget";

// ✅ NEW: Weather Spot widget (Met Office)
import WeatherSpotWidget from "../widgets/WeatherSpotWidget.jsx";

// ✅ NEW: Flood Intelligence widget
import FloodIntelligenceWidget from "../widgets/FloodIntelligenceWidget.jsx";

// ✅ NEW: Traffic Intelligence widget
import TrafficIntelligenceWidget from "../widgets/TrafficIntelligenceWidget.jsx";

import { useTheme } from "../context/ThemeContext.jsx";

const STORAGE_KEY_LAYOUT = "jobsLayout";
const STORAGE_KEY_WIDGETS = "jobsWidgets";

const MOBILE_BREAKPOINT = 768;

function useIsMobile(breakpoint = MOBILE_BREAKPOINT) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return false;
    }

    return window.matchMedia(`(max-width: ${breakpoint}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia(`(max-width: ${breakpoint}px)`);

    const apply = () => {
      setIsMobile(mediaQuery.matches);
    };

    apply();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", apply);
    } else {
      mediaQuery.addListener(apply);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", apply);
      } else {
        mediaQuery.removeListener(apply);
      }
    };
  }, [breakpoint]);

  return isMobile;
}

const defaultLayout = [
  { i: "job_form", x: 0, y: 0, w: 6, h: 5, minW: 4, minH: 4 },
  { i: "job_upload", x: 6, y: 0, w: 6, h: 5, minW: 4, minH: 4 },
  { i: "job_list", x: 0, y: 5, w: 12, h: 6, minW: 6, minH: 5 }, // ✅ Added back
  { i: "job_progress", x: 0, y: 11, w: 6, h: 4, minW: 2, minH: 3 },
  { i: "contact_form", x: 6, y: 11, w: 6, h: 4, minW: 4, minH: 4 },
  { i: "taxonomy_admin", x: 0, y: 15, w: 12, h: 7, minW: 6, minH: 5 },
  { i: "authority_directory", x: 0, y: 22, w: 12, h: 9, minW: 6, minH: 6 },
  { i: "weather_spot_widget", x: 0, y: 31, w: 12, h: 8, minW: 6, minH: 6 },
  { i: "flood_intelligence_widget", x: 0, y: 39, w: 12, h: 8, minW: 6, minH: 6 },

  // ✅ NEW: Traffic Intelligence
  // Keep it after flood so the location-intelligence stack stays grouped together.
  { i: "traffic_intelligence_widget", x: 0, y: 47, w: 12, h: 8, minW: 6, minH: 6 },
];

const normaliseLayout = (layout) =>
  (Array.isArray(layout) ? layout : []).map((item) => {
    const y = Number.isFinite(item?.y) ? item.y : 0;
    return { ...item, y: Math.max(0, y) };
  });

const JobsPage = () => {
  const { theme } = useTheme();
  const isMobile = useIsMobile();
  const [widgets, setWidgets] = useState({});
  const [layout, setLayout] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const gridRef = useRef(null);
  const containerPadding = 24;
  const headerHeight = 64;
  const rowHeight = 40;
  const [gridWidth, setGridWidth] = useState(1200);

  useEffect(() => {
    const node = gridRef.current;
    if (!node) return undefined;

    const updateWidth = () => {
      const nextWidth = Math.floor(node.getBoundingClientRect().width);
      if (nextWidth > 0) {
        setGridWidth(nextWidth);
      }
    };

    updateWidth();

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(node);

    return () => resizeObserver.disconnect();
  }, []);

  const resolveWidgetComponent = (id) => {
    switch (id) {
      case "job_form":
        return <JobFormWidget />;
      case "job_upload":
        return <JobFileUploadWidget />;
      case "job_list":
        return <JobListWidget />;
      case "job_progress":
        return <JobProgressWidget />;
      case "advanced_task_list":
        return <AdvancedTaskListWidget />;
      case "my_tasks":
        return <MyTasksWidget />;
      case "work_package_progress":
        return <WorkPackageProgressWidget />;
      case "contact_form":
        return <ContactFormWidget />;
      case "taxonomy_admin":
        return <TaxonomyAdminWidget />;
      case "work_package_taxonomy_admin":
        return <WorkPackageTaxonomyAdminWidget />;
      case "authority_directory":
        return <AuthorityDirectoryWidget />;
      case "weather_spot_widget":
        return <WeatherSpotWidget />;
      case "flood_intelligence_widget":
        return <FloodIntelligenceWidget />;
      case "traffic_intelligence_widget":
        return <TrafficIntelligenceWidget />;
      default:
        return <div style={{ padding: "1rem", color: "red" }}>Unknown widget: {id}</div>;
    }
  };

  useEffect(() => {
    try {
      const storedLayout = JSON.parse(localStorage.getItem(STORAGE_KEY_LAYOUT));
      const storedWidgetData = JSON.parse(localStorage.getItem(STORAGE_KEY_WIDGETS));

      let isCorrupted = false;
      if (Array.isArray(storedWidgetData)) {
        if (storedWidgetData.some((item) => typeof item !== "string")) {
          isCorrupted = true;
        }
      }

      if (isCorrupted) {
        console.warn("Clearing corrupted JobsPage localStorage...");
        localStorage.removeItem(STORAGE_KEY_LAYOUT);
        localStorage.removeItem(STORAGE_KEY_WIDGETS);
      }

      if (!isCorrupted && Array.isArray(storedLayout) && Array.isArray(storedWidgetData)) {
        const normalised = normaliseLayout(storedLayout);
        setLayout(normalised);

        const components = {};
        storedWidgetData.forEach((id) => {
          components[id] = resolveWidgetComponent(id);
          markWidgetUsedIn(id, "jobs");
        });

        setWidgets(components);
        console.log("✅ Restored widgets from storage:", storedWidgetData);
        return;
      }
    } catch (err) {
      console.error("Failed to load saved widget layout:", err);
    }

    setLayout(defaultLayout);
    setWidgets({
      job_form: <JobFormWidget />,
      job_upload: <JobFileUploadWidget />,
      job_list: <JobListWidget />,
      job_progress: <JobProgressWidget />,
      contact_form: <ContactFormWidget />,
      taxonomy_admin: <TaxonomyAdminWidget />,
      authority_directory: <AuthorityDirectoryWidget />,
      weather_spot_widget: <WeatherSpotWidget />,
      flood_intelligence_widget: <FloodIntelligenceWidget />,
      traffic_intelligence_widget: <TrafficIntelligenceWidget />,
    });

    markWidgetUsedIn("job_form", "jobs");
    markWidgetUsedIn("job_upload", "jobs");
    markWidgetUsedIn("job_list", "jobs");
    markWidgetUsedIn("job_progress", "jobs");
    markWidgetUsedIn("contact_form", "jobs");
    markWidgetUsedIn("taxonomy_admin", "jobs");
    markWidgetUsedIn("authority_directory", "jobs");
    markWidgetUsedIn("weather_spot_widget", "jobs");
    markWidgetUsedIn("flood_intelligence_widget", "jobs");
    markWidgetUsedIn("traffic_intelligence_widget", "jobs");

    console.log("✅ Loaded default widgets for JobsPage");
  }, []);

  const handleAddWidget = (widgetId) => {
    if (widgets[widgetId]) {
      alert("Widget already exists");
      return;
    }

    const component = resolveWidgetComponent(widgetId);
    if (!component) {
      alert("Unknown widget");
      return;
    }

    setWidgets((prev) => ({ ...prev, [widgetId]: component }));
    markWidgetUsedIn(widgetId, "jobs");

    setLayout((prev) => {
      const bottomY = prev.length === 0 ? 0 : Math.max(...prev.map((l) => l.y + l.h));
      const newY = Math.max(0, bottomY);

      const newLayout = [
        ...prev,
        { i: widgetId, x: 0, y: newY, w: 6, h: 4, minW: 4, minH: 3, maxW: 12 },
      ];

      try {
        localStorage.setItem(STORAGE_KEY_LAYOUT, JSON.stringify(newLayout));
        localStorage.setItem(
          STORAGE_KEY_WIDGETS,
          JSON.stringify([...Object.keys({ ...widgets, [widgetId]: true })])
        );
      } catch (err) {
        console.error("Failed to save layout:", err);
      }

      return newLayout;
    });

    setShowAddModal(false);
  };

  const handleRemoveWidget = (widgetId) => {
    setWidgets((prev) => {
      const clone = { ...prev };
      delete clone[widgetId];
      return clone;
    });

    setLayout((prev) => {
      const filtered = prev.filter((item) => item.i !== widgetId);
      try {
        localStorage.setItem(STORAGE_KEY_LAYOUT, JSON.stringify(filtered));
        localStorage.setItem(
          STORAGE_KEY_WIDGETS,
          JSON.stringify(Object.keys(widgets).filter((id) => id !== widgetId))
        );
      } catch (err) {
        console.error("Failed to save layout:", err);
      }
      return filtered;
    });
  };

  const onLayoutChange = useCallback(
    (newLayout) => {
      const normalised = normaliseLayout(newLayout);
      setLayout(normalised);
      try {
        localStorage.setItem(STORAGE_KEY_LAYOUT, JSON.stringify(normalised));
        localStorage.setItem(STORAGE_KEY_WIDGETS, JSON.stringify(Object.keys(widgets)));
      } catch (err) {
        console.error("Failed to save layout:", err);
      }
    },
    [widgets]
  );

  /*
   * Mobile is a presentation of the existing Jobs configuration, not a second
   * editable layout. Follow the saved desktop visual order without persisting
   * mobile positions back into jobsLayout.
   */
  const mobileWidgetEntries = Object.entries(widgets).sort(([a], [b]) => {
    const aIndex = layout.findIndex((item) => item.i === a);
    const bIndex = layout.findIndex((item) => item.i === b);

    const aLayout = aIndex >= 0 ? layout[aIndex] : null;
    const bLayout = bIndex >= 0 ? layout[bIndex] : null;

    const ay = Number.isFinite(aLayout?.y) ? aLayout.y : Number.MAX_SAFE_INTEGER;
    const by = Number.isFinite(bLayout?.y) ? bLayout.y : Number.MAX_SAFE_INTEGER;

    if (ay !== by) return ay - by;

    const ax = Number.isFinite(aLayout?.x) ? aLayout.x : Number.MAX_SAFE_INTEGER;
    const bx = Number.isFinite(bLayout?.x) ? bLayout.x : Number.MAX_SAFE_INTEGER;

    if (ax !== bx) return ax - bx;

    return aIndex - bIndex;
  });

  const getMobileWidgetHeight = (widgetId) => {
    const item = layout.find((layoutItem) => layoutItem.i === widgetId);

    const h = Number.isFinite(item?.h)
      ? Math.max(1, item.h)
      : 4;

    /*
     * Mirror the approximate desktop vertical allocation:
     * rows * rowHeight plus the inter-row grid gaps.
     *
     * A 280px floor prevents compact desktop widgets becoming too short for
     * touch use while preserving the relative size of larger widgets.
     */
    return Math.max(
      280,
      h * rowHeight + Math.max(0, h - 1) * 24
    );
  };

  return (
    <div
      className={isMobile ? undefined : "p-6"}
      style={{
        backgroundColor: "#f0f0f0",
        minHeight: isMobile ? "100%" : "100vh",
        overflowX: "hidden",
        paddingTop: isMobile ? "4px" : undefined,
        paddingBottom: isMobile ? "16px" : undefined,
        paddingLeft: isMobile ? "12px" : containerPadding,
        paddingRight: isMobile ? "12px" : containerPadding,
        boxSizing: "border-box",
      }}
    >
      <div className="w-full">
        <div
          className={isMobile ? undefined : "flex justify-between items-center mb-1"}
          style={{
            minHeight: isMobile ? "58px" : headerHeight,
            height: isMobile ? "auto" : headerHeight,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: isMobile ? "12px" : undefined,
            marginBottom: isMobile ? "6px" : undefined,
          }}
        >
          <h2
            className={isMobile ? undefined : "text-2xl font-semibold"}
            style={{
              fontSize: isMobile ? "1.35rem" : undefined,
              fontWeight: 600,
              lineHeight: 1.2,
              margin: 0,
              minWidth: 0,
            }}
          >
            Job Management
          </h2>
          <button
            onClick={() => setShowAddModal(true)}
            style={{
              backgroundColor: theme.button_action?.normal || "#000",
              color: "#fff",
              padding: isMobile ? "10px 14px" : "10px 18px",
              minHeight: isMobile ? "44px" : undefined,
              whiteSpace: "nowrap",
              borderRadius: "6px",
              fontWeight: "bold",
              fontFamily: "var(--font-body)",
              cursor: "pointer",
              border: "none",
              transition: "background-color 0.2s ease",
            }}
            onMouseEnter={(e) => (e.target.style.backgroundColor = theme.button_action?.hover || "#333")}
            onMouseLeave={(e) => (e.target.style.backgroundColor = theme.button_action?.normal || "#000")}
            onMouseDown={(e) => (e.target.style.backgroundColor = theme.button_action?.click || "#111")}
            onMouseUp={(e) => (e.target.style.backgroundColor = theme.button_action?.hover || "#333")}
          >
            Add Widget
          </button>
        </div>

        <div ref={gridRef} className="w-full" style={{ padding: 0, margin: 0, minWidth: 0 }}>
          {isMobile ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                width: "100%",
                minWidth: 0,
                paddingBottom: "8px",
              }}
            >
              {mobileWidgetEntries.map(([key, WidgetComp]) => (
                <div
                  key={key}
                  style={{
                    width: "100%",
                    minWidth: 0,
                    height: `${getMobileWidgetHeight(key)}px`,
                    overflow: "hidden",
                  }}
                >
                  <WidgetWrapper
                    title={key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                    onRemove={() => handleRemoveWidget(key)}
                  >
                    {WidgetComp}
                  </WidgetWrapper>
                </div>
              ))}
            </div>
          ) : (
            <GridLayout
              className="layout"
              layout={layout}
              cols={12}
              rowHeight={rowHeight}
              width={gridWidth}
              draggableHandle=".widget-drag-handle"
              resizeHandles={["se"]}
              isResizable
              isDraggable
              onLayoutChange={onLayoutChange}
              compactType={null}
              preventCollision={false}
              margin={[16, 24]}
            >
              {Object.entries(widgets).map(([key, WidgetComp]) => (
                <div key={key}>
                  <WidgetWrapper
                    title={key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                    onRemove={() => handleRemoveWidget(key)}
                  >
                    {WidgetComp}
                  </WidgetWrapper>
                </div>
              ))}
            </GridLayout>
          )}
        </div>

        {showAddModal && (
          <AddWidgetModal
            widgets={[
              { id: "job_form", friendly_name: "Create Job" },
              { id: "job_upload", friendly_name: "Upload Files" },
              { id: "job_list", friendly_name: "Job List" },
              { id: "job_progress", friendly_name: "Job Progress" },
              { id: "advanced_task_list", friendly_name: "Advanced Task List" },
              { id: "my_tasks", friendly_name: "My Tasks" },
              { id: "work_package_progress", friendly_name: "Work Package Progress" },
              { id: "contact_form", friendly_name: "Add Contact" },
              { id: "taxonomy_admin", friendly_name: "Taxonomy Admin" },
              { id: "work_package_taxonomy_admin", friendly_name: "Work Package Taxonomy Admin" },
              { id: "authority_directory", friendly_name: "Authority Directory" },
              { id: "weather_spot_widget", friendly_name: "Met Office Weather" },
              { id: "flood_intelligence_widget", friendly_name: "Flood Intelligence" },
              { id: "traffic_intelligence_widget", friendly_name: "Traffic Intelligence" },
            ]}
            onClose={() => setShowAddModal(false)}
            onSelect={handleAddWidget}
          />
        )}
      </div>
    </div>
  );
};

export default JobsPage;